import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import applicationService from "../../services/application/application.service";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { useRef, useState } from "react";
import ArfDataTable, { ColumnMeta } from "../../components/table/ArfDataTable";
import { Button } from "primereact/button";
import AddOrUpdateAuditPlanModal from "./AddOrUpdateAuditPlanModal";
import applicationAuditPlanService from "../../services/application-audit-plans/application-audit-plans.service";
import { ApplicationAuditPlanDto } from "../../services/application-audit-plans/application-audit-plans.dto";
import { UnitType } from "../../services/general-units/units.dto";
import deleteConfirmation from "../../components/confirmations/DeleteConfirmation";
import { useAxiosContext } from "../..";
import moment from "moment";
import { ApplicationStatus } from "../../services/application/application.dto";
import applicationAuditsService from "../../services/application-audits/application-audits.service";

const AuditPlan = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { appUID } = useParams();
  const menu = useRef<Menu>(null);
  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState(false);
  const [selectedAuditPlan, setSelectedAuditPlan] = useState<ApplicationAuditPlanDto | null>(null);
  const toaster = useAxiosContext();

  const { data: applicationFormByUid } = useQuery({
    queryKey: ["applicationFormByUid", appUID],
    queryFn: () =>
      applicationService.GetByUid(appUID || "", {
        blockUI: true,
      }),
    enabled: !!appUID,
  });

  const applicationForm = applicationFormByUid?.result;

  const { data: applicationAuditPlans } = useQuery({
    queryKey: ["applicationAuditPlans", applicationForm?.id],
    queryFn: () =>
      applicationAuditPlanService.GetPlansForApplicationId(applicationForm?.id || 0, {
        blockUI: true,
      }),
    enabled: !!applicationForm?.id,
  });

  var actions: MenuItem[] = [
    {
      label: t("Edit"),
      icon: "pi pi-pencil",
      command: () => {
        setOpenModal(true);
      },
    },
    {
      label: t("Delete"),
      icon: "pi pi-times",
      command: () => {
        deleteConfirmation(t, {
          message: t("DeleteAuditPlanMessage"),
          header: "",
          accept: () =>
            applicationAuditPlanService
              .Delete(selectedAuditPlan?.id || 0, { blockUI: true })
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ["applicationAuditPlans"] });
              }),
          reject: () => {
            setSelectedAuditPlan(null);
          },
        });
      },
    },
  ];

  let columns: ColumnMeta[] = [
    {
      field: "unitName",
      header: t("UnitName"),
      body: (row: ApplicationAuditPlanDto) => {
        const unit = row.agricultureUnit || row.generalUnit || row.greenChemistryUnit;
        const unitId = row.agricultureUnitId || row.generalUnitId || row.greenChemistryUnitId;
        return (
          <div className="p-3 border-round transition-duration-200 surface-card border-2 surface-border">
            <div className="flex flex-column gap-2">
              <div className="flex align-items-center justify-content-between gap-2">
                <span className="font-semibold text-lg white-space-nowrap">
                  {unitId} - {unit?.name}
                </span>
                <span
                  className={`text-xs px-2 py-1 border-round white-space-nowrap ${
                    unit?.type === UnitType.Facility
                      ? "bg-blue-100 text-blue-900"
                      : unit?.type === UnitType.Subcontractor
                        ? "bg-orange-100 text-orange-900"
                        : "bg-green-100 text-green-900"
                  }`}
                >
                  {unit?.type === UnitType.Facility
                    ? t("Facility")
                    : unit?.type === UnitType.Subcontractor
                      ? t("Subcontractor")
                      : t("MainUnit")}
                </span>
              </div>
              <div className="text-sm text-600">
                <i className="pi pi-map-marker mr-2"></i>
                {unit?.district && `${unit?.district}`}
                {unit?.district && unit?.province?.value && `, `}
                {unit?.province?.value}
                {unit?.country?.value && unit?.province?.value && `, `}
                {unit?.country?.value}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      field: "auditPlanDate",
      header: t("Date"),
      body: (row: ApplicationAuditPlanDto) => {
        return (
          <div className="white-space-nowrap">
            {moment(row.auditPlanDate).format("DD.MM.YYYY HH:mm")}
          </div>
        );
      },
    },
    {
      field: "subject",
      header: t("Subject"),
    },

    {
      field: "comment",
      header: t("Comment"),
    },
  ];

  if (applicationForm?.status === ApplicationStatus.WaitingForPlanning) {
    columns.push({
      field: "actions",
      header: t("Actions"),
      body: (row: any) => {
        return (
          <Button
            icon="pi pi-ellipsis-h"
            rounded
            text
            onClick={e => {
              menu?.current?.toggle(e);
              setSelectedAuditPlan(row);
            }}
          />
        );
      },
    });
  }

  const handleSendBackToAssignAuditor = () => {
    applicationAuditsService.AuditorReject(applicationForm?.id || 0, { blockUI: true }).then(() => {
      toaster.showMessage(t("AuditPlanRejected"), "success");
      navigate(`/app/duties`);
    });
  };

  const handleSendToPlanApproval = () => {
    applicationAuditPlanService
      .LeadAuditorPlanApprove(applicationForm?.id || 0, { blockUI: true })
      .then(() => {
        toaster.showMessage(t("AuditPlanSentToPlanApproval"), "success");
        navigate(`/app/duties`);
      });
  };

  const handleApproveAuditPlan = () => {
    applicationAuditsService
      .AuditorApprove(applicationForm?.id || 0, { blockUI: true })
      .then(() => {
        toaster.showMessage(t("AuditPlanApproved"), "success");
        navigate(`/app/duties`);
      });
  };

  return (
    <div className="px-3 md:px-6 py-3 absolute w-full lg:relative">
      <div
        className="flex flex-row gap-2 mb-3 w-max hover-opacity-70 cursor-pointer"
        onClick={() => navigate(`/app/duties/detail/${applicationForm?.id}`)}
      >
        <div className="flex align-items-center">
          <i className="pi pi-arrow-left text-base"></i>
        </div>
        <div className="flex align-items-center font-medium text-xl">{t("AuditDetailPlan")}</div>
      </div>

      <Menu model={actions} popup ref={menu} />
      <ArfDataTable
        data={{
          data: applicationAuditPlans?.result || [],
          totalCount: applicationAuditPlans?.totalCount || 0,
        }}
        columns={columns}
        filterOptions={{
          enabled: false,
        }}
        actionOptions={{
          text: t("AddAuditPlan"),
          command() {
            setSelectedAuditPlan(null);
            setOpenModal(true);
          },
          enabled: applicationForm?.status === ApplicationStatus.WaitingForPlanning,
        }}
        exportOptions={{
          enabled: false,
        }}
        emptyMessageOptions={{
          text: t("EmptyAuditPlanTableMessage"),
          enabled: false,
        }}
        paginatorOptions={{
          enabled: false,
        }}
      />
      {openModal && (
        <AddOrUpdateAuditPlanModal
          isVisible={openModal}
          onClose={() => {
            setOpenModal(false);
            setSelectedAuditPlan(null);
          }}
          selectedAuditPlan={selectedAuditPlan}
          applicationId={applicationForm?.id || 0}
          sectorName={applicationForm?.sector.name || ""}
        />
      )}
      <div className="flex justify-content-between mt-4">
        <Button
          label={
            applicationForm?.status === ApplicationStatus.WaitingForPlanning
              ? t("SendBackToAssignAuditor")
              : t("RejectAuditPlan")
          }
          onClick={handleSendBackToAssignAuditor}
          severity="danger"
        />
        <Button
          label={
            applicationForm?.status === ApplicationStatus.WaitingForPlanning
              ? t("SendToPlanApproval")
              : t("ApproveAuditPlan")
          }
          onClick={
            applicationForm?.status === ApplicationStatus.WaitingForPlanning
              ? handleSendToPlanApproval
              : handleApproveAuditPlan
          }
          disabled={applicationAuditPlans?.result?.length === 0}
        />
      </div>
    </div>
  );
};

export default AuditPlan;
